import React, { useState, useEffect, useMemo } from 'react';
import { Star, TrendingUp, Award, ShoppingCart, Loader, Info, Clock, Navigation, Zap, Package, Flame, Plus, Minus, Utensils } from 'lucide-react';
import { getFirstImageUrl, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import { useOnlineKitchensWithFood, FoodItem } from '../Utils/useOnlineKitchensWithFood';
import { formatFoodCategory } from '../Utils/category';
import { useCart } from '../Utils/useCart';
import FoodDetailModal from '../FoodDetailModal/FoodDetailModal';
import ExpandableText from '../Utils/ExpandableText';
import LoginPromptModal from '../LoginPromptModal/LoginPromptModal';
import Skeleton from '../Utils/Skeleton';
import './FoodDiscovery.css';

type Tab = 'recommended' | 'top_rated' | 'trending';

interface DiscoveryFood {
  food_id: number;
  food_name: string;
  image_urls?: string | string[];
  food_image?: string;
  price?: number;
  average_rating?: number;
  total_reviews?: number;
  total_orders?: number;
  category?: string;
  description?: string;
  available_quantity?: number;
  recommendation_score?: number;
  trending_score?: number;
  rating_score?: number;
  preparation_time?: string;
  pickup_location?: string;
  serving_size?: string;
  stock_status?: string;
  badges?: string[];
  distance_km?: number;
  eta?: string;
  delivery_status?: string;
  is_available?: boolean;
  kitchen?: { kitchen_id: number; kitchen_name: string; city?: string };
}

interface FoodDiscoveryProps {
  onCartChange?: () => void;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  cartRefreshToken?: number;
  locationVersion?: number;
}

const FALLBACK = FOOD_FALLBACK_IMG;

const FoodDiscovery: React.FC<FoodDiscoveryProps> = ({ onCartChange, showNotification, cartRefreshToken = 0, locationVersion = 0 }) => {
  const [activeTab, setActiveTab] = useState<Tab>('recommended');
  const [radius, setRadius] = useState<number>(() => {
    const stored = localStorage.getItem('foodDiscoveryRadius');
    return stored ? parseInt(stored, 10) : 15;
  });
  const { meals, isLoading, error } = useOnlineKitchensWithFood(locationVersion, radius);
  const [foods, setFoods] = useState<DiscoveryFood[]>([]);
  const [detailFood, setDetailFood] = useState<DiscoveryFood | null>(null);
  const [visibleCount, setVisibleCount] = useState(8);
  const [quickFilter, setQuickFilter] = useState<'all' | 'near' | 'available' | 'delivery'>('all');
  const isLoggedIn = !!localStorage.getItem('authToken');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const { cartMap, handleIncrement, handleDecrement } = useCart<DiscoveryFood>({
    cartRefreshToken,
    onCartChange,
    isAuthenticated: isLoggedIn,
    analyticsEventName: 'add_to_cart',
    getId: food => food.food_id,
    getName: food => food.food_name,
    onAuthRequired: () => setShowLoginPrompt(true),
    onCartConflict: () => showNotification?.('warning', 'Different Kitchen', 'Clear your current cart to order from this kitchen.'),
    onAddFailed: (_name, message) => showNotification?.('error', 'Failed', message || 'Could not add to cart.'),
    onNetworkError: () => showNotification?.('error', 'Network Error', 'Please check your connection.'),
  });

  useEffect(() => {
    const mapped: DiscoveryFood[] = meals.map((m: FoodItem) => ({
      food_id: m.id,
      food_name: m.food_name,
      image_urls: m.image_url,
      price: m.price,
      average_rating: m.average_rating,
      total_reviews: m.total_reviews ?? m.review_count,
      total_orders: m.total_orders,
      category: m.category,
      description: m.description,
      available_quantity: m.available_quantity,
      trending_score: m.trending_score,
      rating_score: m.rating_score,
      stock_status: m.stock_status,
      badges: m.badges,
      distance_km: m.distance_km ?? undefined,
      eta: m.eta,
      delivery_status: m.delivery_status,
      kitchen: { kitchen_id: m.kitchen_id, kitchen_name: m.kitchen_name },
    }));
    const sorted = [...mapped];
    if (activeTab === 'top_rated') {
      sorted.sort((a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0));
    } else if (activeTab === 'trending') {
      sorted.sort((a, b) => (b.total_orders ?? 0) - (a.total_orders ?? 0));
    } else {
      // "For You" — backend does not return recommendation_score yet, so use
      // trending_score + rating_score as a fallback. When all scores are equal
      // (e.g. all zero), the sort is stable and preserves API order.
      sorted.sort((a, b) => ((b.trending_score ?? 0) + (b.rating_score ?? 0)) - ((a.trending_score ?? 0) + (a.rating_score ?? 0)));
    }
    setFoods(sorted);
    setVisibleCount(8);
  }, [meals, activeTab]);

  const getImageUrl = (food: DiscoveryFood): string => {
    if (food.food_image) return getFirstImageUrl(food.food_image);
    return getFirstImageUrl(food.image_urls);
  };

  const handleRadiusChange = (r: number) => {
    setRadius(r);
    localStorage.setItem('foodDiscoveryRadius', String(r));
  };

  const filteredFoods = useMemo(() => {
    switch (quickFilter) {
      case 'near':
        return foods.filter(f => f.distance_km !== undefined && f.distance_km <= 5);
      case 'available':
        return foods.filter(f => f.available_quantity === undefined || f.available_quantity > 0);
      case 'delivery':
        return foods.filter(f => !!f.delivery_status);
      default:
        return foods;
    }
  }, [foods, quickFilter]);

  const visibleFoods = filteredFoods.slice(0, visibleCount);
  const hasMore = filteredFoods.length > visibleCount;

  const tabs: { key: Tab; label: string; Icon: React.ElementType }[] = [
    { key: 'recommended', label: 'For You', Icon: Award },
    { key: 'top_rated', label: 'Top Rated', Icon: Star },
    { key: 'trending', label: 'Trending', Icon: TrendingUp },
  ];

  return (
    <section className="fd-section">
      <div className="fd-header">
        <div className="fd-title-group">
          <h2 className="fd-title">Discover Food</h2>
          <p className="fd-subtitle">Personalized picks, fan favorites, and what's hot right now</p>
        </div>
        <div className="fd-tabs">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`fd-tab${activeTab === key ? ' fd-tab--active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="fd-controls">
        <label className="fd-radius-label">Radius:</label>
        <select
          className="fd-radius-select"
          value={radius}
          onChange={e => handleRadiusChange(Number(e.target.value))}
        >
          {[5, 10, 15, 20, 25, 30].map(r => (
            <option key={r} value={r}>{r} km</option>
          ))}
        </select>
      </div>

      {!isLoading && !error && foods.length > 0 && (
        <div className="fd-quick-filters">
          <button
            className={`fd-quick-chip ${quickFilter === 'all' ? 'fd-quick-chip--active' : ''}`}
            onClick={() => setQuickFilter('all')}
          >
            All
          </button>
          <button
            className={`fd-quick-chip ${quickFilter === 'near' ? 'fd-quick-chip--active' : ''}`}
            onClick={() => setQuickFilter('near')}
          >
            <Navigation size={12} /> Under 5 km
          </button>
          <button
            className={`fd-quick-chip ${quickFilter === 'available' ? 'fd-quick-chip--active' : ''}`}
            onClick={() => setQuickFilter('available')}
          >
            <Package size={12} /> Available Now
          </button>
          <button
            className={`fd-quick-chip ${quickFilter === 'delivery' ? 'fd-quick-chip--active' : ''}`}
            onClick={() => setQuickFilter('delivery')}
          >
            <Zap size={12} /> Delivery
          </button>
        </div>
      )}

      {isLoading && (
        <div className="fd-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="fd-skeleton-card">
              <Skeleton width="100%" height="100%" borderRadius="0" style={{ aspectRatio: '4 / 3' }} />
              <div className="fd-skeleton-body">
                <Skeleton width="70%" height="0.9rem" style={{ marginBottom: '0.4rem' }} />
                <Skeleton width="50%" height="0.72rem" style={{ marginBottom: '0.4rem' }} />
                <Skeleton width="100%" height="0.72rem" style={{ marginBottom: '0.2rem' }} />
                <Skeleton width="80%" height="0.72rem" style={{ marginBottom: '0.5rem' }} />
                <div className="fd-skeleton-footer">
                  <Skeleton width="3rem" height="0.95rem" />
                  <Skeleton width="5rem" height="2rem" borderRadius="8px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div className="fd-error">{error}</div>
      )}

      {!isLoading && !error && foods.length === 0 && (
        <div className="fd-empty">
          <Utensils size={48} color="#bbb" />
          <p>No dishes available right now. Check back soon!</p>
        </div>
      )}

      {!isLoading && !error && foods.length > 0 && (
        <>
        <div className="fd-grid">
          {visibleFoods.map(food => {
            const imgUrl = getImageUrl(food);
            const cartItem = cartMap[food.food_id];
            const qty = cartItem?.quantity ?? 0;
            const cartLoading = cartItem?.loading ?? false;
            const soldOut = food.available_quantity === 0 || food.stock_status === 'Sold Out';

            return (
              <div key={food.food_id} className={`fd-card ${soldOut ? 'fd-card--soldout' : ''}`}>
                <div className="fd-card-img-wrap">
                  <img
                    src={imgUrl}
                    alt={food.food_name}
                    className="fd-card-img"
                    onError={e => { (e.target as HTMLImageElement).src = FALLBACK; }}
                  />
                  <div className="fd-card-overlay" />
                  {soldOut && (
                    <div className="fd-soldout-overlay">
                      <span className="fd-soldout-ribbon">Sold Out</span>
                    </div>
                  )}
                  {food.category && (
                    <span className="fd-category-badge">{formatFoodCategory(food.category)}</span>
                  )}
                  {food.average_rating !== undefined && food.average_rating > 0 && (
                    <span className="fd-rating-badge">
                      <Star size={11} fill="currentColor" />
                      {food.average_rating.toFixed(1)}
                      {food.total_reviews !== undefined && food.total_reviews > 0 && (
                        <span className="fd-reviews"> ({food.total_reviews})</span>
                      )}
                    </span>
                  )}
                  {activeTab === 'trending' && food.total_orders !== undefined && food.total_orders > 0 && (
                    <span className="fd-trending-badge">
                      <TrendingUp size={11} />
                      {food.total_orders} orders
                    </span>
                  )}
                  {food.price !== undefined && (
                    <span className="fd-price-badge">£{food.price}</span>
                  )}
                </div>

                <div className="fd-card-body">
                  <h4 className="fd-card-name">{food.food_name}</h4>
                  {food.kitchen && (
                    <p className="fd-card-kitchen">by {food.kitchen.kitchen_name}</p>
                  )}
                  {food.description && (
                    <ExpandableText text={food.description} limit={70} className="fd-card-desc" />
                  )}
                  <div className="fd-card-badges">
                    {food.badges?.map(b => (
                      <span key={b} className={`fd-badge fd-badge--${b.toLowerCase().replace(/\s+/g, '-')}`}>{b}</span>
                    ))}
                    {food.stock_status && food.stock_status !== 'In Stock' && (
                      <span className="fd-badge fd-badge--stock">{food.stock_status}</span>
                    )}
                    {food.delivery_status && (
                      <span className="fd-badge fd-badge--delivery"><Zap size={10} /> {food.delivery_status}</span>
                    )}
                  </div>
                  <div className="fd-card-meta">
                    {food.distance_km !== undefined && food.distance_km !== null && (
                      <span className="fd-meta-item"><Navigation size={12} /> {food.distance_km.toFixed(1)} km</span>
                    )}
                    {food.eta && (
                      <span className="fd-meta-item"><Clock size={12} /> {food.eta}</span>
                    )}
                    {food.available_quantity !== undefined && food.available_quantity > 0 && (
                      <span className="fd-meta-item"><Package size={12} /> {food.available_quantity} left</span>
                    )}
                  </div>
                  <div className="fd-card-footer">
                    <div className="fd-card-price-row">
                      {food.price !== undefined && (
                        <span className="fd-card-price">£{food.price}</span>
                      )}
                      {activeTab === 'recommended' && food.recommendation_score !== undefined && food.recommendation_score > 0 && (
                        <span className="fd-score fd-score--rec"><Flame size={11} /> {Math.round(food.recommendation_score)}% match</span>
                      )}
                      {activeTab === 'trending' && food.trending_score !== undefined && food.trending_score > 0 && (
                        <span className="fd-score fd-score--trend"><TrendingUp size={11} /> {Math.round(food.trending_score)} trending</span>
                      )}
                    </div>
                    <div className="fd-card-actions">
                      <button
                        className="fd-info-btn"
                        onClick={() => setDetailFood(food)}
                        title="View details & reviews"
                      >
                        <Info size={14} />
                      </button>
                      {food.price !== undefined && !soldOut && (
                        qty > 0 ? (
                          <div className="fd-qty-controls">
                            <button
                              className="fd-qty-btn"
                              onClick={() => handleDecrement(food)}
                              disabled={cartLoading}
                            >
                              {cartLoading ? <Loader size={12} className="fd-spin" /> : <Minus size={14} />}
                            </button>
                            <span className="fd-qty-val">{qty}</span>
                            <button
                              className="fd-qty-btn"
                              onClick={() => handleIncrement(food)}
                              disabled={cartLoading}
                            >
                              {cartLoading ? <Loader size={12} className="fd-spin" /> : <Plus size={14} />}
                            </button>
                          </div>
                        ) : (
                          <button
                            className="fd-add-btn"
                            onClick={() => handleIncrement(food)}
                            disabled={cartLoading}
                          >
                            {cartLoading
                              ? <Loader size={14} className="fd-spin" />
                              : <ShoppingCart size={14} />}
                            {cartLoading ? 'Adding...' : 'Add to Cart'}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <div className="fd-load-more-wrap">
            <button className="fd-load-more" onClick={() => setVisibleCount(c => c + 8)}>
              Load More ({filteredFoods.length - visibleCount} remaining)
            </button>
          </div>
        )}
        {!hasMore && filteredFoods.length > 0 && (
          <p className="fd-results-count">Showing {visibleFoods.length} of {filteredFoods.length} dishes</p>
        )}
        </>
      )}
      {showLoginPrompt && <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />}
      {detailFood && (
        <FoodDetailModal
          foodId={detailFood.food_id}
          onClose={() => setDetailFood(null)}
          showNotification={showNotification}
          onCartChange={onCartChange}
        />
      )}
    </section>
  );
};

export default FoodDiscovery;
